import { Link } from 'react-router-dom';
import { ArrowRight, Check, Lock } from 'lucide-react';
import { PULSE_DEMO_HREF, PULSE_PRICING_HREF } from '@/data/pulse';
import { trackEvent } from '@/lib/analytics';
import { Reveal } from '../Reveal';

// Section 13 — pricing & access. No invented prices: until approved pricing
// is entered in the CMS (with VAT treatment and visibility enabled), the
// configurable pricing note and enquiry actions are shown instead.
export const PulsePricing = ({ sp, listing }) => {
  const showPrices =
    listing?.priceVisible &&
    (listing?.monthlyPrice || listing?.annualPrice || listing?.perEmployeePrice || listing?.setupFee);

  return (
    <section aria-labelledby="pulse-pricing-heading" data-testid="pulse-pricing" className="relative overflow-hidden bg-navy-900 py-20 sm:py-28">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold-500">Pricing & Access</p>
          <h2 id="pulse-pricing-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="pulse-pricing-heading">
            Choose the right Pulse setup
          </h2>

          {showPrices ? (
            <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="pulse-pricing-cards">
              {[
                ['Monthly', listing.monthlyPrice, 'per month'],
                ['Annual', listing.annualPrice, 'per year'],
                ['Per employee', listing.perEmployeePrice, 'per employee / month'],
                ['Setup', listing.setupFee, 'once-off'],
              ].filter(([, v]) => v != null).map(([label, value, unit]) => (
                <div key={label} className="rounded-xl border border-white/10 bg-white/5 p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
                  <p className="mt-2 text-2xl font-extrabold text-white">€{value}</p>
                  <p className="text-xs text-slate-400">{unit}{listing.vatTreatment === 'exclusive' ? ' + VAT' : ''}</p>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300" data-testid="pulse-pricing-note">
                {sp.pricingNote}
              </p>
              <ul className="mx-auto mt-8 grid max-w-2xl gap-x-6 gap-y-2.5 text-left sm:grid-cols-2" aria-label="Available package structures">
                {(sp.pricingModels || []).map((model) => (
                  <li key={model} className="flex items-start gap-2.5 text-sm font-semibold text-slate-200">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                    {model}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to={PULSE_DEMO_HREF}
              data-testid="pulse-pricing-demo"
              onClick={() => trackEvent('pulse_demo_click')}
              className="group inline-flex items-center gap-2 rounded-md bg-gold-500 px-6 py-3.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600"
            >
              Book a Demonstration
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <Link
              to={PULSE_PRICING_HREF}
              data-testid="pulse-pricing-request"
              onClick={() => trackEvent('pulse_pricing_click')}
              className="inline-flex items-center gap-2 rounded-md border-2 border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-gold-500 hover:text-gold-500"
            >
              Request Pricing
            </Link>
          </div>
          {sp.loginUrl && (
            <a
              href={sp.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pulse-pricing-login"
              onClick={() => trackEvent('pulse_login_click')}
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-slate-300 underline-offset-4 transition-colors hover:text-gold-500 hover:underline"
            >
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              Existing Client Login
            </a>
          )}
        </Reveal>
      </div>
    </section>
  );
};
